import React from 'react';
import '../assets/css/layout/legal.css';

const VendorTerms = () => {
    return (
        <div className="legal-container">
            <div className="legal-header">
                <h1>Vendor Agreement</h1>
                <p className="last-updated">Last Updated: January 21, 2026</p>
            </div>

            <div className="legal-content">
                <section className="legal-section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Vendor Agreement (&quot;Agreement&quot;) governs your use of the E-AquaHealth 
                        Marketplace as a vendor. By registering as a vendor on our Platform, you agree 
                        to comply with this Agreement, our general Terms of Service, and Privacy Policy.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>2. Vendor Eligibility</h2>
                    <p>To become a vendor on E-AquaHealth, you must:</p>
                    <ul>
                        <li>Be at least 18 years old or the legal age of majority in your jurisdiction</li>
                        <li>Have a valid business registration or license (where applicable)</li>
                        <li>Provide accurate business and contact information</li>
                        <li>Have the legal right to sell the products you list</li>
                        <li>Comply with all applicable local, state, and federal laws and regulations</li>
                        <li>Maintain any required licenses or permits for selling aquaculture products</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>3. Vendor Registration and Verification</h2>
                    <h3>3.1 Application Process</h3>
                    <p>
                        All vendor applications are subject to review and approval by E-AquaHealth. 
                        We reserve the right to accept or reject any vendor application at our discretion.
                    </p>

                    <h3>3.2 Required Documentation</h3>
                    <p>Vendors may be required to provide:</p>
                    <ul>
                        <li>Business registration certificate</li>
                        <li>Tax identification number</li>
                        <li>Valid government-issued identification</li>
                        <li>Proof of address</li>
                        <li>Bank account information for payouts</li>
                        <li>Product quality certifications (where applicable)</li>
                    </ul>

                    <h3>3.3 Verification Status</h3>
                    <p>
                        Vendor accounts may be marked as &quot;Verified&quot; after successful verification of 
                        submitted documents. Verified status may be revoked if documentation expires or 
                        if violations of this Agreement occur.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>4. Product Listings</h2>
                    <h3>4.1 Product Requirements</h3>
                    <p>All products listed must:</p>
                    <ul>
                        <li>Be accurately described with truthful information</li>
                        <li>Include clear, high-quality images of the actual product</li>
                        <li>Have accurate pricing in the designated currency</li>
                        <li>Specify shipping information and delivery timeframes</li>
                        <li>Comply with all applicable safety standards and regulations</li>
                        <li>Not infringe on any intellectual property rights</li>
                    </ul>

                    <h3>4.2 Prohibited Products</h3>
                    <p>The following products are prohibited on E-AquaHealth:</p>
                    <ul>
                        <li>Counterfeit or replica products</li>
                        <li>Illegally sourced fish, wildlife, or aquatic organisms</li>
                        <li>Banned chemicals, pesticides, or medications</li>
                        <li>Products that violate import/export regulations</li>
                        <li>Products without required safety certifications</li>
                        <li>Expired products (medications, feeds, treatments)</li>
                        <li>Products that make false health or efficacy claims</li>
                    </ul>

                    <h3>4.3 Product Quality</h3>
                    <p>
                        Vendors are responsible for ensuring all products meet the quality standards 
                        described in their listings. E-AquaHealth may remove listings that receive 
                        consistent quality complaints or negative reviews.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>5. Pricing and Payments</h2>
                    <h3>5.1 Pricing</h3>
                    <p>
                        Vendors set their own product prices. All prices must be displayed in Nigerian 
                        Naira (NGN) unless otherwise specified. Prices must include all applicable taxes 
                        or clearly state if taxes are additional.
                    </p>

                    <h3>5.2 Commission and Fees</h3>
                    <p>E-AquaHealth charges the following fees:</p>
                    <ul>
                        <li><strong>Platform Commission:</strong> 10% of the product sale price</li>
                        <li><strong>Payment Processing Fee:</strong> 1.5% per transaction</li>
                        <li><strong>Listing Fee:</strong> Free for up to 50 products; contact us for higher volumes</li>
                    </ul>
                    <p>
                        Fees are subject to change with 30 days&apos; notice. Current fee schedules are 
                        always available in your vendor dashboard.
                    </p>

                    <h3>5.3 Payouts</h3>
                    <p>
                        Vendor earnings are paid out according to the following schedule:
                    </p>
                    <ul>
                        <li>Payouts are processed weekly on Fridays</li>
                        <li>Minimum payout threshold: ₦5,000</li>
                        <li>Funds are held for 7 days after delivery confirmation before release</li>
                        <li>Payouts are made to the bank account on file</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>6. Order Fulfillment</h2>
                    <h3>6.1 Processing Time</h3>
                    <p>
                        Vendors must process and ship orders within the timeframe specified in their 
                        listings (maximum 3 business days unless otherwise stated). Failure to meet 
                        processing times may result in penalties or account suspension.
                    </p>

                    <h3>6.2 Shipping Requirements</h3>
                    <ul>
                        <li>Provide accurate tracking information for all shipments</li>
                        <li>Use appropriate packaging to prevent damage during transit</li>
                        <li>For perishable items, use appropriate cold chain logistics</li>
                        <li>Clearly label packages with handling instructions where necessary</li>
                    </ul>

                    <h3>6.3 Order Cancellation</h3>
                    <p>
                        Vendors may cancel orders only in exceptional circumstances (e.g., out of stock, 
                        product discontinuation). Excessive cancellations may result in account review 
                        and potential suspension.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>7. Returns and Refunds</h2>
                    <h3>7.1 Return Policy</h3>
                    <p>
                        Vendors must accept returns for products that are:
                    </p>
                    <ul>
                        <li>Damaged during shipping</li>
                        <li>Defective or not as described</li>
                        <li>Incorrect items shipped</li>
                        <li>Expired or near expiration (for dated products)</li>
                    </ul>

                    <h3>7.2 Refund Processing</h3>
                    <p>
                        Refunds must be processed within 5 business days of receiving a valid return. 
                        E-AquaHealth may deduct refund amounts from vendor payouts for buyer protection claims.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>8. Customer Service</h2>
                    <p>Vendors are expected to:</p>
                    <ul>
                        <li>Respond to customer inquiries within 24 hours</li>
                        <li>Maintain professional and courteous communication</li>
                        <li>Resolve disputes in good faith</li>
                        <li>Cooperate with E-AquaHealth in investigating complaints</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>9. Performance Standards</h2>
                    <p>Vendors must maintain the following performance metrics:</p>
                    <ul>
                        <li><strong>Order Fulfillment Rate:</strong> Minimum 95%</li>
                        <li><strong>On-Time Shipping Rate:</strong> Minimum 90%</li>
                        <li><strong>Customer Rating:</strong> Minimum 3.5 out of 5 stars</li>
                        <li><strong>Response Time:</strong> Within 24 hours for messages</li>
                        <li><strong>Refund Rate:</strong> Below 5%</li>
                    </ul>
                    <p>
                        Vendors falling below these standards may receive warnings, temporary suspension, 
                        or permanent removal from the Platform.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>10. Intellectual Property</h2>
                    <p>
                        Vendors retain ownership of their product content but grant E-AquaHealth a 
                        non-exclusive license to display, promote, and market their products on the 
                        Platform. Vendors must not use any E-AquaHealth trademarks or branding without 
                        written permission.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>11. Confidentiality</h2>
                    <p>
                        Vendors agree to keep confidential any proprietary information shared by 
                        E-AquaHealth, including but not limited to business strategies, customer data, 
                        and Platform algorithms. Customer information obtained through the Platform 
                        may only be used for order fulfillment purposes.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>12. Account Suspension and Termination</h2>
                    <h3>12.1 Grounds for Suspension</h3>
                    <p>E-AquaHealth may suspend vendor accounts for:</p>
                    <ul>
                        <li>Violation of this Agreement or Terms of Service</li>
                        <li>Fraudulent activity or misrepresentation</li>
                        <li>Selling prohibited products</li>
                        <li>Consistently poor performance metrics</li>
                        <li>Multiple customer complaints</li>
                        <li>Non-compliance with legal requirements</li>
                    </ul>

                    <h3>12.2 Termination</h3>
                    <p>
                        Either party may terminate this Agreement with 30 days&apos; written notice. 
                        E-AquaHealth may terminate immediately for serious violations. Upon termination, 
                        vendors must fulfill all pending orders and outstanding obligations.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>13. Indemnification</h2>
                    <p>
                        Vendors agree to indemnify and hold harmless E-AquaHealth, its affiliates, 
                        officers, directors, and employees from any claims, damages, or expenses arising 
                        from: (a) products sold through the Platform, (b) violation of this Agreement, 
                        (c) infringement of any third-party rights, or (d) violation of any applicable laws.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>14. Limitation of Liability</h2>
                    <p>
                        E-AquaHealth is not liable for any indirect, incidental, special, or consequential 
                        damages arising from this Agreement. Our total liability shall not exceed the 
                        commissions earned by E-AquaHealth from the vendor in the twelve (12) months 
                        preceding the claim.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>15. Dispute Resolution</h2>
                    <p>
                        Any disputes arising from this Agreement shall first be resolved through good 
                        faith negotiation. If unresolved within 30 days, disputes shall be submitted to 
                        binding arbitration in Lagos, Nigeria, in accordance with applicable arbitration rules.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>16. Modifications to Agreement</h2>
                    <p>
                        E-AquaHealth may modify this Agreement at any time. Vendors will be notified of 
                        material changes via email and Platform notification at least 30 days before 
                        changes take effect. Continued use of the Platform after changes constitutes 
                        acceptance of the modified Agreement.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>17. Contact Information</h2>
                    <p>
                        For questions about this Vendor Agreement or vendor support, please contact us at:
                    </p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> vendors@eaquahealth.com</p>
                        <p><strong>Vendor Support:</strong> support@eaquahealth.com</p>
                        <p><strong>Address:</strong> E-AquaHealth Headquarters</p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default VendorTerms;
